import { CIMSColumn } from '../../CIMSColumn/types'
import { CIMSTags } from 'components/CIMSTags'
import { Role as R, User as U } from 'schema'
import { CIMSTagsTag } from '../../../CIMSTags/types'
import { TableFilterItem } from 'hooks/useTableFilter/types'

interface UseUsersAndRolesColumnOptions {
  title: React.ReactNode
  help: string
}

type User = Pick<U, 'id' | 'displayName' | 'mail'>
type Role = Pick<R, 'id' | 'name'> & { users: Pick<U, 'id' | 'deleted'>[] }

export function useUsersAndRolesColumn<
  T extends { users: User[]; roles: Role[] }
>({ help, title }: UseUsersAndRolesColumnOptions): CIMSColumn<T> {
  return {
    title,
    help,
    width: 300,
    render: ({ roles, users }) => {
      const roleTags = roles.map<CIMSTagsTag>(({ name, users }) => ({
        text: name,
        color: 'processing',
        tooltip: `${users.length} users`,
      }))
      const userTags = users.map<CIMSTagsTag>(({ displayName, mail, id }) => {
        const userInRoles = roles.some(({ users }) =>
          users.some((user) => user.id === id)
        )
        return {
          text: displayName ?? mail,
          color: userInRoles ? 'warning' : undefined,
          tooltip: userInRoles
            ? `${
                displayName ?? mail
              } can be removed from this list. They are already a member of at least 1 associated role`
            : undefined,
        }
      })
      return <CIMSTags tags={[...roleTags, ...userTags]} />
    },
    filter: {
      with: 'callback',
      items: (records) => {
        const users = records.flatMap(({ users }) => users)
        const roles = records.flatMap(({ roles }) => roles)
        const items: TableFilterItem[] = []
        if (users.length > 0)
          items.push({
            text: 'Users',
            value: 'users',
            children: users.map(({ displayName, mail, id }) => ({
              text: displayName ?? mail,
              tooltip: !!displayName ? mail : undefined,
              value: id,
            })),
          })
        if (roles.length > 0)
          items.push({
            text: 'Roles',
            value: 'roles',
            children: roles.map(({ id, name }) => ({
              text: name,
              value: id,
            })),
          })
        return items
      },
      onFilter: (value, { roles, users }) =>
        roles.some(({ id }) => id === value) ||
        users.some(({ id }) => value === id),
    },
  }
}
